import { useEffect, useRef, useState } from 'react';

const services = [
  {
    icon: 'ri-user-line',
    title: 'Tailor-Made Suits',
    description: 'Full measurement and fitting service for the perfect suit. Every piece is crafted to your exact specifications and body measurements.'
  },
  {
    icon: 'ri-shirt-line',
    title: 'Coats, Shirts & Trousers',
    description: 'Premium everyday and event wear tailored to perfection. From casual elegance to formal sophistication.'
  },
  {
    icon: 'ri-scissors-line',
    title: 'Alterations & Repairs',
    description: 'Expert fixing of length, waist, sleeves and more. We ensure your existing garments fit like they were made for you.'
  },
  {
    icon: 'ri-team-line',
    title: 'Wedding & Group Packages',
    description: 'Special packages for grooms and groomsmen. Make your special day unforgettable with coordinated, perfectly fitted suits.'
  }
];

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-32 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-950/5 to-transparent pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
            <span className="text-xs uppercase tracking-[0.2em] text-amber-400 font-medium">
              What We Offer
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Services
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Comprehensive tailoring solutions for the modern gentleman
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-700 delay-${index * 100} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {/* Card */}
              <div className="relative h-full p-8 bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-gray-800 hover:border-amber-500/50 transition-all duration-300 overflow-hidden">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/0 group-hover:from-amber-500/5 group-hover:to-transparent transition-all duration-300"></div>
                
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/40 transition-all duration-300 group-hover:scale-110">
                    <i className={`${service.icon} text-3xl text-black`}></i>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold mb-3 text-white group-hover:text-amber-400 transition-colors" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {service.description}
                </p>

                {/* Corner accent */}
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-amber-500/0 group-hover:border-amber-500/30 transition-all duration-300 rounded-br-2xl"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
